import { useState } from 'react';
import { X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../hooks/useSubscription';
import { openLeadJobSalesModal } from '../lib/billing/leadJobSalesModal';

const URGENT_DAYS = 3;

function readDismissed(): boolean {
  try {
    return sessionStorage.getItem('trial_status_banner_dismissed') === 'true';
  } catch {
    return false;
  }
}

function dayLabel(days: number | null | undefined): string {
  if (days == null) return '—';
  if (days <= 0) return 'ends today';
  if (days === 1) return '1 day left';
  return `${days} days left`;
}

/**
 * Trial status chrome — Operate whisper by default.
 * No white marketing card; escalate only near expiry / when expired.
 */
const TrialStatusBanner = () => {
  const { entitlements, loading } = useSubscription();
  const [dismissed, setDismissed] = useState(readDismissed);
  const navigate = useNavigate();

  const handleDismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem('trial_status_banner_dismissed', 'true');
    } catch {
      /* ignore */
    }
  };

  if (dismissed) return null;

  const { access, daysRemaining, billingHold } = entitlements;

  if (loading && access === 'none') return null;
  if (billingHold === 'grace' || billingHold === 'paused') return null;

  if (access === 'expired') {
    return (
      <div
        role="status"
        className="flex flex-wrap items-center justify-between gap-3 py-1"
        data-testid="trial-status-banner"
      >
        <div className="min-w-0 max-w-[65ch]">
          <p className="text-sm font-semibold text-neutral-90">Trial ended</p>
          <p className="text-[13px] text-neutral-90/55 mt-0.5 leading-snug">
            Platform access is paused. Talk to sales to reactivate halr.
          </p>
        </div>
        <button
          type="button"
          onClick={() => openLeadJobSalesModal('research_expired')}
          className="inline-flex items-center justify-center min-h-11 px-4 text-sm font-medium rounded-btn bg-neutral-90 text-white hover:bg-neutral-90/85 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-neutral-90 shrink-0"
        >
          Talk to sales
        </button>
      </div>
    );
  }

  if (access !== 'trialing') return null;

  const days = daysRemaining ?? null;
  const urgent = days != null && days <= URGENT_DAYS;

  return (
    <div
      role="status"
      className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 py-1"
      data-testid="trial-status-banner"
    >
      <p className="text-[13px] leading-snug text-neutral-90/55 min-w-0">
        <span className={urgent ? 'font-medium text-neutral-90' : 'text-neutral-90/70'}>
          Trial · {dayLabel(days)}
        </span>
        <span className="hidden sm:inline">
          {' '}
          — LinkedIn-only trial. Email starts on a paid plan. Calling is Enterprise.
        </span>
      </p>
      <div className="flex items-center gap-1 shrink-0">
        <button
          type="button"
          onClick={() => navigate('/app/billing')}
          className="inline-flex items-center justify-center min-h-11 px-2 text-[13px] font-medium text-neutral-90 underline-offset-2 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-neutral-90"
        >
          Upgrade
        </button>
        <button
          type="button"
          onClick={handleDismiss}
          className="inline-flex items-center justify-center min-h-11 min-w-11 rounded-btn text-neutral-90/40 hover:bg-neutral-90/5 hover:text-neutral-90/70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-neutral-90"
          aria-label="Dismiss trial status"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default TrialStatusBanner;
